import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileTextIcon } from "@phosphor-icons/react";
import { SiteIcon } from "@libs/zapi-ui/ds/components/site-icon";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { SyncedSpinner } from "@/zapi-frontend/shared/ui/SyncedSpinner";
import type { TLedgerFigure } from "@/zapi-frontend/shared/types/modelLedger";
import {
  documentOrigin,
  formatFigureValue,
  formatMoveDate,
  formatValue,
  legacyMove,
  sourceBucket,
  type TLedgerSourceBucket
} from "./ledgerFormat";
import {
  BINDING_LABEL_KEY,
  CONFIDENCE_LABEL_KEY,
  PROVENANCE_LABEL_KEY,
  ROLE_LABEL_KEY,
  SOURCE_LABEL_KEY,
  TREATMENT_LABEL_KEY
} from "./ledgerLabels";
import { Formula, SourceCell, SourceText, ValueCell } from "./_LedgerCells";
import {
  BODY_TEXT,
  FIGURE_LINK,
  LINK,
  LedgerFacts,
  LedgerPanel,
  LedgerPanelDivider,
  LedgerPanelSection,
  LedgerRow as LedgerRowLayout,
  type TLedgerKind
} from "./_LedgerParts";

export type TSourceView = "vault" | "projectFrame";

const kindOf = (bucket: TLedgerSourceBucket): TLedgerKind =>
  bucket === "calculated" ? "calculated" : bucket === "assumption" ? "assumption" : "sourced";

function LastMove({ figure }: { figure: TLedgerFigure }) {
  const { t, i18n } = useTranslation("projectHome");
  const move = figure.moves?.at(-1);
  const legacy = legacyMove(figure.source?.ref);
  if (move) {
    const values = {
      date: formatMoveDate(move.at, i18n.language),
      from: formatValue(move.from, figure.unit, figure.dp),
      to: formatValue(move.to, figure.unit, figure.dp)
    };
    return (
      <p className={BODY_TEXT} data-testid={`ledger-row-${figure.key}-move`}>
        {move.by ? t("modelLedger.movedBy", { ...values, by: move.by }) : t("modelLedger.moved", values)}
      </p>
    );
  }
  if (!legacy) return null;
  return (
    <p className={BODY_TEXT} data-testid={`ledger-row-${figure.key}-move`}>
      {t("modelLedger.movedEarlier", { from: formatValue(legacy.was, figure.unit, figure.dp) })}
    </p>
  );
}

// Where the figure comes from: its formula, the page it was read on, the document or the brief
// it was taken from, or the reasoning behind a number the model posited.
function Origin({
  figure,
  byKey,
  onReveal,
  onOpenView
}: {
  figure: TLedgerFigure;
  byKey: Map<string, TLedgerFigure>;
  onReveal: (key: string) => void;
  onOpenView: (view: TSourceView, fileName?: string | null) => void | Promise<void>;
}) {
  const { t } = useTranslation("projectHome");
  const [downloading, setDownloading] = useState(false);
  if (figure.kind === "computed" && figure.formula) {
    return (
      <Formula
        formula={figure.formula}
        byKey={byKey}
        onReveal={onReveal}
        equals={formatValue(figure.value, figure.unit, figure.dp)}
      />
    );
  }
  const legacy = legacyMove(figure.source?.ref);
  const source =
    figure.source && legacy
      ? { ...figure.source, kind: figure.source.url ? ("web" as const) : figure.source.kind, ref: legacy.original }
      : figure.source;
  if (!source) return <span className={BODY_TEXT}>—</span>;
  const note = source.ref ? <SourceText text={source.ref} /> : null;
  if (source.kind === "web" && source.url) {
    return (
      <div className="flex flex-col gap-1">
        <a
          href={source.url}
          target="_blank"
          rel="noreferrer"
          className={cn(LINK, "typo-label-md w-fit max-w-full")}
          title={source.title || source.url}
          data-testid={`ledger-row-${figure.key}-origin-link`}
        >
          <SiteIcon href={source.url} />
          <span className="truncate">{source.title || source.url}</span>
        </a>
        {note}
      </div>
    );
  }
  if (source.kind === "document" || source.kind === "brief") {
    const view: TSourceView = source.kind === "document" ? "vault" : "projectFrame";
    const cited = source.kind === "document" ? documentOrigin(source.ref) : null;
    const label =
      cited?.fileName ?? t(source.kind === "document" ? "modelLedger.originDocument" : "modelLedger.sourceBrief");
    const where = cited ? cited.where : source.ref;

    // Finding and fetching the file takes a few seconds, so the link says it is working and takes
    // no second click until the download starts or the Vault opens instead.
    const openOrigin = async () => {
      if (!cited?.fileName) {
        onOpenView(view);
        return;
      }
      setDownloading(true);
      await onOpenView(view, cited.fileName);
      setDownloading(false);
    };
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={downloading}
            onClick={openOrigin}
            className={cn(
              LINK,
              "typo-label-md w-fit min-w-0 wrap-break-word text-left",
              downloading && "cursor-default opacity-60"
            )}
            data-testid={`ledger-row-${figure.key}-origin-link`}
          >
            <FileTextIcon aria-hidden="true" />
            {label}
          </button>
          {downloading && (
            <span
              className="typo-caption flex shrink-0 items-center gap-1.5 text-muted-foreground"
              data-testid={`ledger-row-${figure.key}-origin-downloading`}
            >
              <SyncedSpinner className="size-3" />
              {t("modelLedger.downloading")}
            </span>
          )}
        </div>
        {where && <SourceText text={where} />}
      </div>
    );
  }
  return note ?? <span className={BODY_TEXT}>{t(SOURCE_LABEL_KEY[sourceBucket(figure)])}</span>;
}

function WhatThisIs({
  figure,
  byKey,
  narrow,
  onReveal
}: {
  figure: TLedgerFigure;
  byKey: Map<string, TLedgerFigure>;
  narrow: boolean;
  onReveal: (key: string) => void;
}) {
  const { t } = useTranslation("projectHome");
  const figureLinks = (keys: string[]) => (
    <span className="flex flex-wrap gap-x-3 gap-y-1">
      {keys.map((key) => (
        <button key={key} type="button" onClick={() => onReveal(key)} className={FIGURE_LINK}>
          {byKey.get(key)?.name ?? key}
        </button>
      ))}
    </span>
  );
  const facts: [string, ReactNode][] = [];
  if (narrow && figure.role) facts.push([t("modelLedger.columnRole"), t(ROLE_LABEL_KEY[figure.role])]);
  if (figure.binding) facts.push([t("modelLedger.factBinding"), t(BINDING_LABEL_KEY[figure.binding])]);
  if (figure.provenance) {
    facts.push([
      t("modelLedger.factProvenance"),
      <span key="provenance" className={cn(figure.provenance === "engine" && "text-destructive")}>
        {t(PROVENANCE_LABEL_KEY[figure.provenance])}
      </span>
    ]);
  }
  if (figure.confidence) facts.push([t("modelLedger.factConfidence"), t(CONFIDENCE_LABEL_KEY[figure.confidence])]);
  if (figure.treatment) facts.push([t("modelLedger.factTreatment"), t(TREATMENT_LABEL_KEY[figure.treatment])]);
  if (figure.asOf) facts.push([t("modelLedger.factAsOf"), figure.asOf]);
  if (figure.assumedFrom?.length) facts.push([t("modelLedger.factAssumedFrom"), figureLinks(figure.assumedFrom)]);
  if (figure.relatedTo?.length) facts.push([t("modelLedger.factRelatedTo"), figureLinks(figure.relatedTo)]);
  if (facts.length === 0) return null;
  return (
    <LedgerPanelSection heading={t("modelLedger.whatThisIs")} testId={`ledger-row-${figure.key}-what`}>
      <LedgerFacts facts={facts} />
    </LedgerPanelSection>
  );
}

function DrivesRow({
  dependent,
  byKey,
  onReveal
}: {
  dependent: TLedgerFigure;
  byKey: Map<string, TLedgerFigure>;
  onReveal: (key: string) => void;
}) {
  const value = formatValue(dependent.value, dependent.unit, dependent.dp);
  return (
    <li className="flex min-w-0 flex-col gap-1.5">
      <button
        type="button"
        onClick={() => onReveal(dependent.key)}
        data-testid={`ledger-drives-${dependent.key}`}
        className="typo-label-md w-fit rounded-sm text-left text-foreground outline-none hover:underline focus-visible:ring-3 focus-visible:ring-ring/50"
      >
        {dependent.name}
      </button>
      {dependent.formula ? (
        <Formula formula={dependent.formula} byKey={byKey} onReveal={onReveal} equals={value} />
      ) : (
        <span className="typo-label-md tabular-nums">{value}</span>
      )}
    </li>
  );
}

function RowPanel({
  figure,
  byKey,
  dependents,
  narrow,
  onReveal,
  onOpenView
}: {
  figure: TLedgerFigure;
  byKey: Map<string, TLedgerFigure>;
  dependents: TLedgerFigure[];
  narrow: boolean;
  onReveal: (key: string) => void;
  onOpenView: (view: TSourceView, fileName?: string | null) => void | Promise<void>;
}) {
  const { t } = useTranslation("projectHome");
  return (
    <LedgerPanel testId={`ledger-row-${figure.key}-details`}>
      {figure.description && (
        <p
          className={cn(BODY_TEXT, "max-w-[80ch] wrap-break-word")}
          data-testid={`ledger-row-${figure.key}-description`}
        >
          {figure.description}
        </p>
      )}
      <LedgerPanelSection heading={t("modelLedger.whereFrom")}>
        <Origin figure={figure} byKey={byKey} onReveal={onReveal} onOpenView={onOpenView} />
        <LastMove figure={figure} />
      </LedgerPanelSection>
      {figure.error && (
        <LedgerPanelSection heading={t("modelLedger.whyNotComputed")} testId={`ledger-row-${figure.key}-why`}>
          <span className="typo-body-sm text-destructive">{figure.error}</span>
        </LedgerPanelSection>
      )}
      {figure.unverified && (
        <LedgerPanelSection heading={t("modelLedger.unverified")} testId={`ledger-row-${figure.key}-unverified-why`}>
          <p className={BODY_TEXT}>
            {t("modelLedger.unverifiedWhy", {
              printed: formatFigureValue(figure.unverified.printed),
              computed: formatFigureValue(figure.value)
            })}
          </p>
        </LedgerPanelSection>
      )}
      <WhatThisIs figure={figure} byKey={byKey} narrow={narrow} onReveal={onReveal} />
      {dependents.length > 0 && (
        <>
          <LedgerPanelDivider />
          <LedgerPanelSection heading={t("modelLedger.drives")}>
            <ol className="flex flex-col gap-3">
              {dependents.map((dependent) => (
                <DrivesRow key={dependent.key} dependent={dependent} byKey={byKey} onReveal={onReveal} />
              ))}
            </ol>
          </LedgerPanelSection>
        </>
      )}
    </LedgerPanel>
  );
}

export function LedgerRow({
  figure,
  byKey,
  dependents,
  open,
  highlighted,
  narrow,
  onToggle,
  onReveal,
  onOpenView
}: {
  figure: TLedgerFigure;
  byKey: Map<string, TLedgerFigure>;
  dependents: TLedgerFigure[];
  open: boolean;
  highlighted: boolean;
  narrow: boolean;
  onToggle: (key: string) => void;
  onReveal: (key: string) => void;
  onOpenView: (view: TSourceView, fileName?: string | null) => void | Promise<void>;
}) {
  const { t } = useTranslation("projectHome");
  const bucket = sourceBucket(figure);
  return (
    <LedgerRowLayout
      rowKey={figure.key}
      testId={`ledger-row-${figure.key}`}
      open={open}
      highlighted={highlighted}
      narrow={narrow}
      kind={kindOf(bucket)}
      kindLabel={t(SOURCE_LABEL_KEY[bucket])}
      name={figure.name}
      description={figure.description}
      source={<SourceCell figure={figure} />}
      role={
        figure.role && (
          <span className="typo-caption block truncate text-muted-foreground">{t(ROLE_LABEL_KEY[figure.role])}</span>
        )
      }
      value={<ValueCell figure={figure} />}
      onToggle={() => onToggle(figure.key)}
      panel={
        open && (
          <RowPanel
            figure={figure}
            byKey={byKey}
            dependents={dependents}
            narrow={narrow}
            onReveal={onReveal}
            onOpenView={onOpenView}
          />
        )
      }
    />
  );
}
